import * as React from "react";
import {
  AlertTriangle,
  Ban,
  BadgeCheck,
  Bell,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  X,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import type { DataCatalogAlert } from "../../lib/api/products";
import {
  ALERT_SEVERITY_LEVELS,
  alertOverridesEqual,
  cloneAlertPrefs,
  countAlertPrefsChanges,
  listActiveExceptions,
  moveAlertToList,
  partitionAlerts,
  type AlertPrefsDraft,
} from "./productAlertSubscription";
import { AlertSeverityToggles } from "./AlertSeverityToggles";
import { useI18n } from "../../lib/i18n";
import * as s from "./ProductSubscriptionsModal.css";
import * as ts from "../../../ui/components/Table/Table.css";
import clsx from "clsx";

export type ProductAlertConfigureModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  prefs: AlertPrefsDraft;
  /** Last-saved prefs; used for dirty tracking. Null when never saved. */
  baselinePrefs?: AlertPrefsDraft | null;
  /** Persist prefs (and related subscription batch) to the API. */
  onSave: (prefs: AlertPrefsDraft) => Promise<void>;
  alerts: DataCatalogAlert[];
  alertsLoading?: boolean;
  disabled?: boolean;
  productName?: string;
};

type AlertRow = {
  code: string;
  name: string;
  type: string;
  severity: number;
  isException: boolean;
};

const PAGE_SIZE = 10;

const EMPTY_PREFS: AlertPrefsDraft = {
  enabledSeverities: [],
  overrides: [],
};

const SEVERITY_LABEL: Record<number, string> = Object.fromEntries(
  ALERT_SEVERITY_LEVELS.map((x) => [x.level, x.label]),
);

function TypeIcon({ type }: { type: string }) {
  const isFault = type.toLowerCase() === "fault";
  const Icon = isFault ? AlertTriangle : Bell;
  return <Icon size={11} strokeWidth={2.25} aria-hidden />;
}

export function ProductAlertConfigureModal({
  open,
  setOpen,
  prefs,
  baselinePrefs = null,
  onSave,
  alerts,
  alertsLoading,
  disabled,
  productName,
}: ProductAlertConfigureModalProps) {
  const { t } = useI18n();
  const [draft, setDraft] = React.useState<AlertPrefsDraft>(EMPTY_PREFS);
  const [tab, setTab] = React.useState<"allowed" | "blocked">("allowed");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const compareBaseline = baselinePrefs ?? EMPTY_PREFS;

  React.useEffect(() => {
    if (!open) return;
    setDraft(cloneAlertPrefs(prefs));
    setPage(1);
    setSelected(new Set());
    setSearch("");
    setTab("allowed");
    setError(null);
    setSaving(false);
    // Snapshot prefs only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-gate
  }, [open]);

  const activeAlerts = React.useMemo(
    () => alerts.filter((a) => a.isActive !== false),
    [alerts],
  );

  const { allowed, blocked } = React.useMemo(
    () => partitionAlerts(draft, activeAlerts),
    [draft, activeAlerts],
  );

  const exceptions = React.useMemo(
    () => listActiveExceptions(draft, activeAlerts),
    [draft, activeAlerts],
  );

  const exceptionsDirty = React.useMemo(
    () => !alertOverridesEqual(draft, compareBaseline),
    [draft, compareBaseline],
  );

  const dirtyCount = React.useMemo(
    () => countAlertPrefsChanges(draft, compareBaseline),
    [draft, compareBaseline],
  );
  const isDirty = dirtyCount > 0;

  const rows: AlertRow[] = tab === "allowed" ? allowed : blocked;
  const searchTrimmed = search.trim();
  const hasSearch = searchTrimmed.length > 0;

  const filtered = React.useMemo(() => {
    const q = searchTrimmed.toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.type.toLowerCase().includes(q),
    );
  }, [rows, searchTrimmed]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  React.useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [tab, search, draft.enabledSeverities.join(","), draft.overrides.length]);

  const moveCodes = (codes: string[], target: "allowed" | "blocked") => {
    if (disabled || saving || codes.length === 0) return;
    let next = draft;
    for (const code of codes) {
      const def = activeAlerts.find(
        (a) => a.code.toUpperCase() === code.toUpperCase(),
      );
      next = moveAlertToList(next, code, def?.severity ?? 3, target);
    }
    setDraft(next);
    setSelected(new Set());
  };

  const toggleSelected = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSave = async () => {
    if (disabled || saving || !isDirty) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(cloneAlertPrefs(draft));
      setOpen(false);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Failed to save alert preferences";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (saving) return;
    setOpen(false);
  };

  const clearSearch = () => setSearch("");

  const exceptionTooltip =
    exceptions.length === 0
      ? exceptionsDirty
        ? "Exception list changed (unsaved)"
        : undefined
      : [
          ...(exceptionsDirty ? ["Exception list changed (unsaved)"] : []),
          ...exceptions.map(
            (e) =>
              `${e.code} (${e.mode === "allow" ? "allowed" : "blocked"}) — ${e.name}`,
          ),
        ].join("\n");

  const controlsDisabled = Boolean(disabled || saving);
  const showExceptionsBadge = exceptions.length > 0 || exceptionsDirty;
  const exceptionsLabel = `${exceptions.length} ${
    exceptions.length === 1 ? "exception" : "exceptions"
  }`;
  const listNoun = tab === "allowed" ? "allowed alerts" : "blocked alerts";
  const emptyTitle = hasSearch
    ? `No ${listNoun} matching “${searchTrimmed}”`
    : `No ${listNoun}`;
  const selectedCount = selected.size;
  const bulkTarget = tab === "allowed" ? "blocked" : "allowed";
  const bulkLabel =
    selectedCount > 0
      ? `Add ${selectedCount} to ${bulkTarget}`
      : `Add to ${bulkTarget}`;

  return (
    <Modal
      open={open}
      setOpen={(next) => {
        if (saving && !next) return;
        setOpen(next);
      }}
      minWidth="640px"
      maxWidth="760px"
      closeOnClickOff={!isDirty && !saving}
    >
      <Modal.Header>
        <Text level="h2" weight="bold">
          Configure alerts
        </Text>
        <Text
          level="body-small"
          style={{ color: "var(--color-fgMuted)", marginTop: 4, display: "block" }}
        >
          {productName
            ? `Choose which alerts notify you for ${productName}.`
            : "Choose which alerts notify you for this product."}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div className={s.alertConfigureBody}>
          {error ? (
            <Text level="body-small" style={{ color: "var(--color-danger)" }}>
              {error}
            </Text>
          ) : null}

          <div className={s.alertConfigureSeverityBlock}>
            <AlertSeverityToggles
              prefs={draft}
              onChange={setDraft}
              baselinePrefs={compareBaseline}
              showLabels
              centered
              disabled={controlsDisabled}
            />
            {showExceptionsBadge ? (
              <span
                className={clsx(
                  s.exceptionsBadge,
                  exceptionsDirty && s.dirtyControl,
                )}
                data-unsaved={exceptionsDirty ? "true" : undefined}
                title={exceptionTooltip}
                aria-label={
                  exceptionsDirty
                    ? `${exceptionsLabel} (unsaved)`
                    : exceptionsLabel
                }
              >
                <ShieldAlert size={12} />
                {exceptionsLabel}
              </span>
            ) : null}
          </div>

          <div className={s.alertListHeader}>
            <TabSwitcher
              aria-label={t("pro.products.alerts.filterListsAria")}
              value={tab}
              onChange={(v) => setTab(v as "allowed" | "blocked")}
              tabs={[
                { value: "allowed", label: `Allowed (${allowed.length})` },
                { value: "blocked", label: `Blocked (${blocked.length})` },
              ]}
            />
          </div>

          <div className={s.alertSearchWrap}>
            <input
              className={s.alertSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("pro.products.alerts.searchPlaceholder")}
              disabled={controlsDisabled || alertsLoading}
              aria-label={t("pro.products.alerts.searchAria")}
            />
            {search ? (
              <button
                type="button"
                className={s.alertSearchClear}
                onClick={clearSearch}
                aria-label={t("shared.filter.clearSearch")}
                title={t("shared.filter.clearSearch")}
                disabled={controlsDisabled}
              >
                <X size={14} strokeWidth={2.25} />
              </button>
            ) : null}
          </div>

          <div className={s.alertResults}>
            {alertsLoading ? (
              <div className={s.alertEmpty}>
                <span>{t("pro.products.alerts.loading")}</span>
              </div>
            ) : activeAlerts.length === 0 ? (
              <div className={s.alertEmpty}>
                <span className={s.alertEmptyTitle}>
                  No alert definitions available for this product’s catalog.
                </span>
              </div>
            ) : pageRows.length === 0 ? (
              <div className={s.alertEmpty}>
                <span className={s.alertEmptyTitle}>{emptyTitle}</span>
                {hasSearch ? (
                  <button
                    type="button"
                    className={s.alertEmptyAction}
                    onClick={clearSearch}
                  >
                    Clear search
                  </button>
                ) : null}
              </div>
            ) : (
              <div className={s.alertStack}>
                {pageRows.map((row) => (
                  <div key={row.code} className={s.alertListCard}>
                    <label className={s.alertListCheck}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.code)}
                        disabled={controlsDisabled}
                        onChange={() => toggleSelected(row.code)}
                      />
                    </label>
                    <div className={s.alertListCardMain}>
                      <span className={s.alertListCode} title={row.name}>
                        {row.name}
                      </span>
                      <div className={s.alertListSubtitle}>
                        <span className={s.alertListName} title={row.code}>
                          {row.code}
                        </span>
                        <span
                          className={s.alertCombinedBadge}
                          data-level={row.severity}
                          title={`${SEVERITY_LABEL[row.severity] ?? row.severity} · ${row.type}`}
                        >
                          <TypeIcon type={row.type} />
                          {SEVERITY_LABEL[row.severity] ?? `L${row.severity}`}
                        </span>
                      </div>
                    </div>
                    <div className={s.alertListMeta}>
                      {row.isException ? (
                        <span className={s.alertExceptionChip}>{t("pro.products.alerts.exception")}</span>
                      ) : null}
                      {!controlsDisabled ? (
                        <button
                          type="button"
                          className={s.alertMoveButton}
                          onClick={() =>
                            moveCodes(
                              [row.code],
                              tab === "allowed" ? "blocked" : "allowed",
                            )
                          }
                        >
                          {tab === "allowed" ? "Block" : "Allow"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!controlsDisabled && selectedCount > 0 ? (
              <div className={ts.selectionBarOverlay} role="presentation">
                <div
                  className={ts.selectionBar}
                  role="toolbar"
                  aria-label={t("pro.products.alerts.bulkActionsAria")}
                >
                  <span className={ts.selectionBarCount}>
                    {selectedCount} selected
                  </span>
                  <div className={ts.selectionBarSep} />
                  <button
                    type="button"
                    className={ts.selectionBarBtn}
                    onClick={() =>
                      moveCodes(
                        [...selected],
                        tab === "allowed" ? "blocked" : "allowed",
                      )
                    }
                  >
                    {tab === "allowed" ? (
                      <Ban size={14} aria-hidden />
                    ) : (
                      <BadgeCheck size={14} aria-hidden />
                    )}
                    {bulkLabel}
                  </button>
                  <div className={ts.selectionBarSep} />
                  <button
                    type="button"
                    className={ts.selectionBarBtn}
                    onClick={() => setSelected(new Set())}
                    aria-label={t("shared.listView.clearSelection")}
                    title={t("shared.listView.clearSelection")}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {filtered.length > PAGE_SIZE ? (
            <div className={s.alertPager}>
              <button
                type="button"
                className={s.alertPagerBtn}
                disabled={safePage <= 1 || controlsDisabled}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={t("pro.products.alerts.previousPage")}
              >
                <ChevronLeft size={14} />
              </button>
              <Text level="body-small" color="muted">
                {safePage} / {totalPages}
              </Text>
              <button
                type="button"
                className={s.alertPagerBtn}
                disabled={safePage >= totalPages || controlsDisabled}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label={t("pro.products.alerts.nextPage")}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 28 }}>
          {isDirty ? <UnsavedChangesChip count={dirtyCount} /> : null}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button type="button" color="neutral" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            onClick={() => void handleSave()}
            disabled={controlsDisabled || !isDirty}
            loading={saving}
          >
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
